import { useEffect, useState } from 'react'
import { useRoute, Link } from 'wouter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Calendar, DollarSign, MapPin, Clock, User, Building, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { buildApiUrl } from '@/config/api'
import { useAuth } from '@/contexts/auth-context'
import toast from 'react-hot-toast'

interface InvitationData {
  id: string
  message?: string
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED'
  expiresAt: string
  createdAt: string
  connection: {
    id: string
    name: string
    email?: string
    company?: string
    position?: string
  }
  rfp: {
    id: string
    title: string
    slug: string
    description: string
    category: string
    workType: 'REMOTE' | 'ONSITE' | 'HYBRID'
    country?: string
    budgetMin: number
    budgetMax: number
    budgetCurrency: string
    deadline: string
    status: string
    createdAt: string
    user: {
      id: string
      name: string
      company: string
    }
  }
  inviter: {
    id: string
    name: string
    company: string
  }
}

export default function InvitationPage() {
  const [, params] = useRoute('/invitation/:invitationId')
  const { user } = useAuth()
  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invitationId = params?.invitationId

  useEffect(() => {
    if (invitationId) {
      fetchInvitation()
    }
  }, [invitationId])

  const fetchInvitation = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/linkedin-connections/invitation/${invitationId}`))
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch invitation')
      }

      const data = await response.json()
      setInvitation(data.data)
    } catch (error) {
      console.error('Error fetching invitation:', error)
      setError(error instanceof Error ? error.message : 'Failed to fetch invitation')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAccept = async () => {
    if (!user) {
      toast.error('Please log in to accept this invitation')
      return
    }

    setIsProcessing(true)
    try {
      const response = await fetch(buildApiUrl(`/api/linkedin-connections/invitation/${invitationId}/accept`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to accept invitation')
      }

      await response.json()
      toast.success('Invitation accepted successfully!')
      
      // Refresh invitation data
      await fetchInvitation()
    } catch (error) {
      console.error('Error accepting invitation:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to accept invitation')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDecline = async () => {
    if (!user) {
      toast.error('Please log in to decline this invitation')
      return
    }

    setIsProcessing(true)
    try {
      const response = await fetch(buildApiUrl(`/api/linkedin-connections/invitation/${invitationId}/decline`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to decline invitation')
      }

      toast.success('Invitation declined')
      
      // Refresh invitation data
      await fetchInvitation()
    } catch (error) {
      console.error('Error declining invitation:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to decline invitation')
    } finally {
      setIsProcessing(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Pending</Badge>
      case 'ACCEPTED':
        return <Badge variant="outline" className="text-green-600 border-green-600">Accepted</Badge>
      case 'DECLINED':
        return <Badge variant="outline" className="text-red-600 border-red-600">Declined</Badge>
      case 'EXPIRED':
        return <Badge variant="outline" className="text-gray-600 border-gray-600">Expired</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const getWorkTypeIcon = (workType: string) => {
    switch (workType) {
      case 'REMOTE':
        return '🏠'
      case 'ONSITE':
        return '🏢'
      case 'HYBRID':
        return '🏠🏢'
      default:
        return '💼'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-600">Invitation Not Found</CardTitle>
            <CardDescription>
              {error || 'This invitation could not be found or may have expired.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link href="/">
              <Button>Go to Homepage</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  const isExpired = new Date() > new Date(invitation.expiresAt)
  const canRespond = invitation.status === 'PENDING' && !isExpired

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">RFP Invitation</h1>
          <p className="text-gray-600">
            You've been invited to view a Request for Proposal
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Invitation Details */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Invitation Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">From</p>
                  <p className="font-medium">{invitation.inviter.name}</p>
                  {invitation.inviter.company && (
                    <p className="text-sm text-gray-600">{invitation.inviter.company}</p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-gray-600">To</p>
                  <p className="font-medium">{invitation.connection.name}</p>
                  {invitation.connection.company && (
                    <p className="text-sm text-gray-600">{invitation.connection.company}</p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  {getStatusBadge(invitation.status)}
                </div>

                <div>
                  <p className="text-sm text-gray-600">Expires</p>
                  <p className="text-sm">
                    {new Date(invitation.expiresAt).toLocaleDateString()} at{' '}
                    {new Date(invitation.expiresAt).toLocaleTimeString()}
                  </p>
                </div>

                {invitation.message && (
                  <div>
                    <p className="text-sm text-gray-600">Personal Message</p>
                    <p className="text-sm bg-gray-50 p-3 rounded border italic">
                      "{invitation.message}"
                    </p>
                  </div>
                )}

                {canRespond && (
                  <div className="space-y-2 pt-4 border-t">
                    {!user ? (
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-3">
                          Please log in to respond to this invitation
                        </p>
                        <Link href="/login">
                          <Button className="w-full">Log In</Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Button
                          onClick={handleAccept}
                          disabled={isProcessing}
                          className="w-full"
                        >
                          {isProcessing ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Accept Invitation
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleDecline}
                          disabled={isProcessing}
                          className="w-full"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {invitation.status === 'ACCEPTED' && (
                  <div className="pt-4 border-t">
                    <Link href={`/rfp/${invitation.rfp.slug}`}>
                      <Button className="w-full">
                        View RFP Details
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* RFP Details */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  {invitation.rfp.title}
                </CardTitle>
                <CardDescription>
                  {invitation.rfp.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Budget</p>
                        <p className="font-medium">
                          {invitation.rfp.budgetCurrency} {invitation.rfp.budgetMin.toLocaleString()} - {invitation.rfp.budgetMax.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Deadline</p>
                        <p className="font-medium">
                          {new Date(invitation.rfp.deadline).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getWorkTypeIcon(invitation.rfp.workType)}</span>
                      <div>
                        <p className="text-sm text-gray-600">Work Type</p>
                        <p className="font-medium">{invitation.rfp.workType}</p>
                      </div>
                    </div>

                    {invitation.rfp.country && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-sm text-gray-600">Location</p>
                          <p className="font-medium">{invitation.rfp.country}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Category</p>
                      <Badge variant="outline">{invitation.rfp.category}</Badge>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <Badge variant="outline">{invitation.rfp.status}</Badge>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Posted</p>
                        <p className="font-medium">
                          {new Date(invitation.rfp.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {canRespond && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-4">
                        {user 
                          ? 'Would you like to accept this invitation to view the full RFP details?'
                          : 'Please log in to respond to this invitation.'
                        }
                      </p>
                      {user ? (
                        <div className="flex gap-3 justify-center">
                          <Button onClick={handleAccept} disabled={isProcessing}>
                            {isProcessing ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-2" />
                            )}
                            Accept & View RFP
                          </Button>
                          <Button variant="outline" onClick={handleDecline} disabled={isProcessing}>
                            <XCircle className="h-4 w-4 mr-2" />
                            Decline
                          </Button>
                        </div>
                      ) : (
                        <Link href="/login">
                          <Button>Log In to Respond</Button>
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {invitation.status === 'ACCEPTED' && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="text-center">
                      <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p className="text-green-600 font-medium mb-4">Invitation Accepted!</p>
                      <Link href={`/rfp/${invitation.rfp.slug}`}>
                        <Button>View Full RFP Details</Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
